import { getUserCardStageFromIntervalDays } from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import type {
  CardTypeKey,
  LearningStatsRecentStageEntriesData,
  ProgressStageKey,
  RecentStageEntryCard,
} from "@-/dev.improve.haniverse.lib.learning-stats.entities";

/** How many recently entered cards each Overall-tab stage surface lists: one tile row. */
export const RECENT_STAGE_ENTRY_LIMIT = 8;

const STAGES: readonly ProgressStageKey[] = ["new", "familiar", "proficient", "mastered"];

/** An active card in its current stage, with the fields that date its entry into that stage. */
export type RecentStageEntryCandidate = {
  cardType: CardTypeKey;
  lastReview: string | null;
  manuallyMasteredAt: string | null;
  pinyin: string | null;
  stage: ProgressStageKey;
  startedStudyingAt: string | null;
  subject: string;
  userCardId: string;
};

type Entry = { candidate: RecentStageEntryCandidate; enteredAt: number };

type PendingCard = {
  candidate: RecentStageEntryCandidate;
  /** The oldest review seen so far that left the card in its current stage. */
  earliestStageReviewAt: number | null;
};

function toTime(iso: string | null): number {
  if (iso === null) {
    return 0;
  }
  const time = Date.parse(iso);
  return Number.isFinite(time) ? time : 0;
}

function emptyByStage<T>(create: () => T): Record<ProgressStageKey, T> {
  return { familiar: create(), mastered: create(), new: create(), proficient: create() };
}

/**
 * Finds, per stage, the cards that entered their current stage most recently.
 *
 * A card's entry time is known up front when nothing but a timestamp put it there: manual mastery
 * (`manuallyMasteredAt`) or a never-reviewed New card (`startedStudyingAt`). Every other card
 * entered its stage at a review, found by feeding reviews NEWEST FIRST: each review log is a
 * BEFORE-review snapshot, so the first (newest) review whose snapshot is in a different stage is
 * the one that moved the card into its current stage.
 *
 * Because reviews arrive newest first, entries are discovered in descending time order, so a
 * stage is settled once it has {@link RECENT_STAGE_ENTRY_LIMIT} entries at or after the scan's
 * cursor (or no card of that stage is still unresolved). `isDone()` lets the reader stop paging
 * as soon as every stage is settled instead of scanning the whole review history.
 */
export function createRecentStageEntriesAccumulator(
  candidates: readonly RecentStageEntryCandidate[],
  limit = RECENT_STAGE_ENTRY_LIMIT,
) {
  const knownEntries = emptyByStage<Entry[]>(() => []);
  const reviewEntries = emptyByStage<Entry[]>(() => []);
  const pendingCounts = emptyByStage(() => 0);
  const pendingByCardId = new Map<string, PendingCard>();
  let cursor = Number.POSITIVE_INFINITY;

  for (const candidate of candidates) {
    if (candidate.manuallyMasteredAt !== null) {
      knownEntries.mastered.push({ candidate, enteredAt: toTime(candidate.manuallyMasteredAt) });
    } else if (candidate.lastReview === null) {
      knownEntries[candidate.stage].push({
        candidate,
        enteredAt: toTime(candidate.startedStudyingAt),
      });
    } else {
      pendingByCardId.set(candidate.userCardId, { candidate, earliestStageReviewAt: null });
      pendingCounts[candidate.stage] += 1;
    }
  }

  function isStageSettled(stage: ProgressStageKey) {
    if (pendingCounts[stage] === 0) {
      return true;
    }
    let settledCount = reviewEntries[stage].length;
    for (const entry of knownEntries[stage]) {
      if (entry.enteredAt >= cursor) {
        settledCount += 1;
      }
    }
    return settledCount >= limit;
  }

  return {
    /** Feed reviews in DESCENDING `reviewedAt` order. `scheduledDays` is the before-review interval. */
    addReview(review: { cardId: string; reviewedAt: string; scheduledDays: number | null }) {
      const reviewedAt = toTime(review.reviewedAt);
      cursor = Math.min(cursor, reviewedAt);
      const pending = pendingByCardId.get(review.cardId);
      if (pending === undefined) {
        return;
      }
      const { stage } = pending.candidate;
      if (
        review.scheduledDays !== null &&
        getUserCardStageFromIntervalDays(review.scheduledDays) !== stage
      ) {
        pendingByCardId.delete(review.cardId);
        pendingCounts[stage] -= 1;
        reviewEntries[stage].push({ candidate: pending.candidate, enteredAt: reviewedAt });
        return;
      }
      pending.earliestStageReviewAt = reviewedAt;
    },
    /**
     * `exhausted`: whether every review was fed. After an early stop, an unresolved card entered
     * its stage before the cursor — older than every settled entry — so it is left out, except a
     * New card started after the cursor: all of its reviews were seen, so it has been New since
     * it started.
     */
    finish({ exhausted }: { exhausted: boolean }): LearningStatsRecentStageEntriesData {
      // Cards whose seen history never left their stage: a New card has been New since it started;
      // any other stage is missing the review that moved it (imported history), so use the oldest
      // review that already showed it in the stage.
      const fallbackEntries = emptyByStage<Entry[]>(() => []);
      for (const { candidate, earliestStageReviewAt } of pendingByCardId.values()) {
        const enteredAt =
          candidate.stage === "new"
            ? toTime(candidate.startedStudyingAt)
            : (earliestStageReviewAt ?? toTime(candidate.lastReview));
        if (exhausted || (candidate.stage === "new" && enteredAt > cursor)) {
          fallbackEntries[candidate.stage].push({ candidate, enteredAt });
        }
      }
      const result = emptyByStage<readonly RecentStageEntryCard[]>(() => []);
      for (const stage of STAGES) {
        result[stage] = [...knownEntries[stage], ...reviewEntries[stage], ...fallbackEntries[stage]]
          .sort((a, b) => b.enteredAt - a.enteredAt)
          .slice(0, limit)
          .map(({ candidate, enteredAt }) => ({
            cardType: candidate.cardType,
            enteredAt: new Date(enteredAt).toISOString(),
            pinyin: candidate.pinyin,
            subject: candidate.subject,
            userCardId: candidate.userCardId,
          }));
      }
      return result;
    },
    isDone() {
      return STAGES.every((stage) => isStageSettled(stage));
    },
  };
}
